import PropTypes from 'prop-types';
import { set, get, isEmpty } from 'lodash-es';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { parseContent } from 'client/data/cms/content';
import { CmsModel } from 'client/data/models/cms';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';

export const DEFAULT_SUBMODEL = 'default';

const Vehicle = PropTypes.shape({
  make: PropTypes.shape({ name: PropTypes.string, slug: PropTypes.string }),
  model: PropTypes.shape({ name: PropTypes.string, slug: PropTypes.string }),
  submodels: PropTypes.shape({ name: PropTypes.string, slug: PropTypes.string }),
  year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  pubStates: PropTypes.shape(),
});
const LtrtContent = PropTypes.shape({
  articles: PropTypes.arrayOf(
    PropTypes.shape({
      publishDate: PropTypes.string,
      dateModified: PropTypes.string,
      metaDescription: PropTypes.string,
      titleTag: PropTypes.string,
      author: PropTypes.shape({
        authorLink: PropTypes.string,
        authorName: PropTypes.string,
        authorTitle: PropTypes.string,
        photoPath: PropTypes.string,
      }),
      content: PropTypes.arrayOf(
        PropTypes.oneOfType([
          PropTypes.shape({
            content: PropTypes.string,
            subTitle: PropTypes.string,
            title: PropTypes.string,
            type: PropTypes.string,
          }),
          PropTypes.shape({
            type: PropTypes.string,
            links: PropTypes.arrayOf(
              PropTypes.shape({
                href: PropTypes.string,
                captionTranscript: PropTypes.string,
              })
            ),
          }),
          PropTypes.shape({
            type: PropTypes.string,
            videoIds: PropTypes.shape({
              'youtube-videoid': PropTypes.string,
            }),
          }),
        ])
      ),
    })
  ),
  dataPoints: PropTypes.shape({
    milesDrivenTitle: PropTypes.string,
    avgMpgTitle: PropTypes.string,
    miles: PropTypes.string,
    mpg: PropTypes.string,
  }),
  bullets: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.arrayOf(PropTypes.string),
  }),
  vehicle: Vehicle,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  published: PropTypes.string,
  updated: PropTypes.string,
});

export const LtrtEntities = {
  LtrtContent,
  Vehicle,
};

function getContentVehicle(vehicle) {
  return (
    vehicle && {
      make: { name: vehicle.make, slug: makeNiceName(vehicle.make) },
      model: { name: vehicle.model, slug: makeNiceName(vehicle.model) },
      submodels: { name: vehicle.submodel, slug: makeNiceName(vehicle.submodel) },
      year: parseInt(vehicle.year, 10),
      pubStates: {},
    }
  );
}

/**
 * Create Long Term Road Test Content object based on content data.
 * It contains, summary, articles and media for vehicle
 * Example atom file: edmunds/content/research/ram/1500/2019/long-term-road-test/index.atom
 * @param entry ltrt-content entry
 * @return {LtrtContent}
 */
function buildLtrtContent(entry) {
  const bulletsContentMetadata = entry.child('ltrt-bullets').getAllMetadata();
  const dataPoints = entry.child('ltrt-datapoints').getAllMetadata();
  const bullets = {
    label: bulletsContentMetadata.bulletTitle || '',
    value: Object.keys(bulletsContentMetadata)
      .filter(name => name.includes('bullet-'))
      .sort((name1, name2) => parseInt(name1.replace('bullet-', ''), 10) - parseInt(name2.replace('bullet-', ''), 10))
      .map(name => bulletsContentMetadata[name]),
  };
  const title = entry.title;
  const subtitle = entry.subtitle;
  const vehicle = get(entry, 'vehicles[0]');
  const { published, updated } = entry;
  const articles = entry
    .child('ltrt-entry')
    .children()
    .map(article => {
      const authorPath = article.metadata('authorPath').value();
      const content = article.children().map(contentEntry => {
        const { id } = contentEntry;
        if (id.includes('html-')) {
          return {
            type: 'text',
            content: contentEntry.content,
            title: contentEntry.metadata('title').value(),
            subTitle: contentEntry.metadata('subTitle').value(),
          };
        } else if (id.includes('photo-')) {
          return {
            type: 'photos',
            links: contentEntry.links(),
          };
        } else if (id.includes('video-')) {
          return {
            type: 'video',
            videoIds: {
              'youtube-videoid': contentEntry.metadata('youtube-videoid').value(),
            },
          };
        }
        return {};
      });
      set(content, '[0].title', article.metadata('entryTitle').value());
      return {
        authorPath,
        content,
        publishDate: article.metadata('startDateISOFormat').value(),
        dateModified: article.metadata('dateModified').value(),
        metaDescription: article.metadata('metaDescription').value(),
      };
    });

  return {
    articles,
    dataPoints,
    bullets,
    title,
    subtitle,
    published,
    updated,
    vehicle: getContentVehicle(vehicle),
  };
}

function getLongTermRoadData(match, context) {
  const { make, model, year, submodel } = match;
  return context
    .resolveValue(
      `content["/research/${make}/${model}/${
        submodel === DEFAULT_SUBMODEL ? '' : `${submodel}/`
      }${year}/long-term-road-test/index"]`,
      CmsModel
    )
    .then(response => {
      if (isEmpty(response)) {
        throw new Error();
      }

      return buildLtrtContent(parseContent(response));
    })
    .catch(() => null);
}

export const LongTermRoadTestModel = createModelSegment('longTermRoadTest', [
  /**
   * @see buildLongTermRoadTestPath
   */
  {
    path: 'makes["{make}"].models["{model}"].submodels["{submodel}"].years["{year}"].content',
    resolve(match, context) {
      return getLongTermRoadData(match, context).then(longTermRoadTest => {
        if (!longTermRoadTest) {
          return null;
        }
        const promises = longTermRoadTest.articles.map(article =>
          context
            .resolveValue(`content["${article.authorPath}"]`, CmsModel)
            .then(response => {
              set(article, 'author', response.contentMetadata);
              return response;
            })
            .catch(() => {})
        );
        return Promise.all(promises).then(() => longTermRoadTest);
      });
    },
  },
]);

export function buildLongTermRoadTestPath({ make, model, year, submodel }) {
  return `makes["${make}"].models["${model}"].submodels["${submodel || DEFAULT_SUBMODEL}"].years["${year}"].content`;
}
